import { BehavioralMentalStatus } from "@/components/Provider/PatientView/MemberProfile/MemberProfileNotesMentalStatus";

import { BehavioralSymptomTrend } from "./components/MemberProfileNotesSymptomTrend";
import { DischargeContent } from "./(discharge)/DischargeContent";
import { GenericMultiselect } from "./components/MemberProfileNotesGenericMultiselect";
import { GenericSectionNotes } from "./components/MemberProfileNotesGenericNotes";
import { MemberProfileNoteGoalsAndObjectiveDisplay } from "./components/MemberProfileNoteGoalsAndObjectiveDisplay";
import MemberProfileNotesDuration from "./components/MemberProfileNotesDuration";
import { NotesDiagnosis } from "./components/MemberProfileNotesDiagnosis";

export function BehavioralNewNote({
  editable,
  editHandler,
  errors,
  isCallManuallyLinked,
  note,
  patientAnswersEditable,
  profile,
  views,
}) {
  let { createEditView, draftView, profileView } = views;

  if (!editable) {
    patientAnswersEditable = false;
  }
  const content = Object.hasOwn(note, "content") && note.content;

  let behavioralGoals = (
    <MemberProfileNoteGoalsAndObjectiveDisplay
      content={content}
      draftView={draftView}
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let symptomTrendNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
      textareaProps={{
        "cnai-template-field-format": "paragraph",
        "cnai-template-field-id": "symptom-trend-notes",
        "cnai-template-field-instructions":
          "A concise clinical summary (3-10 sentences) of symptom status and relevant trends reported in the session. Cover: current symptom burden and any changes since the last session based on the Pre-Session Survey (overall functioning, PHQ depression items, GAD anxiety items, and any medical/behavioral health visits reported); sobriety or abstinence status if alcohol or substance use is discussed in the Transcript (include number of days free of use if stated); notable symptom patterns or trends mentioned by the patient (e.g. sleep, energy, mood, appetite, irritability); and any acute concerns flagged (e.g. suicidal ideation, crisis visits). Use concise, factual clinical language. Do not reference the patient by name — use 'the patient' or 'the client'.",
        "cnai-template-field-name": "Symptom Trends/Review",
        "cnai-template-field-type": "string",
      }}
    />
  );

  let explorationNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="exploration_notes"
      sectionLabel="Exploration"
      textareaProps={{
        "cnai-template-field-format": "paragraph",
        "cnai-template-field-id": "exploration-notes",
        "cnai-template-field-instructions":
          "A 5-10 sentence clinical narrative summarizing the thematic work of the session. Cover: what the patient externalized or brought to the session (stressors, concerns, presenting themes); therapeutic approaches and techniques used (e.g. CBT, motivational interviewing, narrative questioning, psychoeducation); specific frameworks or tools introduced or revisited (e.g. cognitive reframing, behavioral activation, coping strategies); the patient's engagement, receptivity, or resistance to interventions; and any self-identified goals, intentions, or insights the patient expressed. Use neutral third-person clinical prose. Do not reference the patient by name — use 'the patient' or 'the client'.",
        "cnai-template-field-name": "Exploration",
        "cnai-template-field-type": "string",
      }}
    />
  );

  let mentalStatus = content.mental_status ? (
    <BehavioralMentalStatus
      createEditView={createEditView}
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      showTitle={true}
    />
  ) : (
    <></>
  );

  let symptomTrends =
    content.symptom_trends && content.symptom_trends_substances ? (
      <BehavioralSymptomTrend
        editable={editable}
        editHandler={editHandler}
        note={note}
        patientAnswersEditable={patientAnswersEditable}
        profileView={profileView}
      />
    ) : (
      <></>
    );

  let changes = content.changes ? (
    <GenericMultiselect
      className={!profileView ? "grey-container" : ""}
      editable={patientAnswersEditable}
      editHandler={editHandler}
      labelClass="clr-sec"
      note={note}
      profileView={profileView}
      sectionKey="changes"
      sectionLabel="Changes/Updates"
    />
  ) : (
    <></>
  );

  let healthCareUse = content.health_care_use ? (
    <GenericMultiselect
      className={!profileView ? "grey-container" : ""}
      editable={patientAnswersEditable}
      editHandler={editHandler}
      labelClass="clr-sec"
      note={note}
      profileView={profileView}
      sectionKey="health_care_use"
      sectionLabel="Health Care Use"
    />
  ) : (
    <></>
  );

  let discharges = Object.hasOwn(content, "discharges") ? (
    <DischargeContent
      classes={{
        card: editable ? "CardSeconday" : null,
        colEditable: "col-12",
        colReadonly: "col-5",
      }}
      dischargeValues={note.content.discharges}
      editable={editable}
      setDischargeValues={(val, key, nested) => editHandler("discharges", val, nested)}
    />
  ) : null;

  let diagnosis = (
    <NotesDiagnosis
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profile={profile}
      profileView={profileView}
    />
  );

  let duration = !isCallManuallyLinked ? (
    <MemberProfileNotesDuration
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  ) : (
    <></>
  );

  let bottomArea;

  if (createEditView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  } else if (profileView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  }

  let bottomAreaMobile;

  if (createEditView) {
    bottomAreaMobile = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  } else if (profileView) {
    bottomAreaMobile = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  }

  let otherCss = profileView ? "px-0 mx-0 w-100" : "m-auto";
  let col1Css = profileView ? "col-3" : "col-5";
  let col2Css = profileView ? "col-9 p-0" : "col-7 pl-3 pr-0";

  let desktopContent = (
    <div className="d-none d-md-block d-lg-block">
      <div>{behavioralGoals}</div>

      <div className={`row ${otherCss}`}>
        <div className={`${col1Css} pr-0 pl-0`}>
          <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
            {symptomTrendNotes}
          </div>
          <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
            {explorationNotes}
          </div>
          <div className={profileView ? "p-3 border-right-note" : ""}>{mentalStatus}</div>
        </div>

        <div className={`${col2Css}`}>
          <div className={profileView ? "p-3" : ""}>{symptomTrends}</div>
          <div className={profileView ? "p-3 border-top-note" : ""}>{changes}</div>
          <div className={profileView ? "p-3 border-top-note" : ""}>{healthCareUse}</div>

          {discharges && (
            <div className={profileView ? "p-3 border-top-note" : ""}>{discharges}</div>
          )}
          <div className={profileView ? "p-3" : ""}>{bottomArea}</div>
        </div>
      </div>
    </div>
  );

  let mobileContent = (
    <div className="d-block d-md-none d-lg-none">
      {behavioralGoals}
      {symptomTrends}
      {symptomTrendNotes}
      {explorationNotes}
      {changes}
      {healthCareUse}
      {discharges}
      {bottomAreaMobile}
    </div>
  );

  return (
    <div
      cnai-template-id="behavioral-progress-note"
      cnai-template-name="Behavioral Health Progress Note"
      cnai-template-type="Individual Progress Note"
    >
      {desktopContent}
      {mobileContent}
    </div>
  );
}
